import type { LegalDoc } from "../types";
import { LEGAL_COMPANY as C } from "../company";

export const terms: LegalDoc = {
  slug: "terms",
  title: "Điều khoản sử dụng",
  metaTitle: "Điều khoản sử dụng",
  metaDescription:
    "Điều khoản sử dụng nền tảng Chốt Ca — kết nối người tìm việc ngành F&B với nhà tuyển dụng tại Việt Nam.",
  version: "2.0.0",
  effectiveDate: C.effectiveDate,
  intro: `Tài liệu này (sau đây gọi là **"Điều khoản"**) là thoả thuận pháp lý giữa bạn (**"Người dùng"**) và **${C.legalName}** (**"chúng tôi"**, **"Chốt Ca"**) về việc sử dụng ứng dụng di động, website ${C.websiteUrl}, website ${C.employerUrl} và các dịch vụ liên quan (sau đây gọi chung là **"Nền tảng"**). Bằng cách tạo tài khoản hoặc sử dụng Nền tảng, bạn xác nhận đã đọc, hiểu và đồng ý ràng buộc với Điều khoản này.`,
  sections: [
    {
      title: "1. Định nghĩa",
      items: [
        "**Nền tảng**: ứng dụng Chốt Ca trên iOS và Android, website chotca.com, website tuyendung.chotca.com và các dịch vụ trực tuyến liên quan.",
        "**Người tìm việc**: cá nhân sử dụng ứng dụng di động để tạo hồ sơ, tìm và ứng tuyển việc làm ngành F&B.",
        "**Nhà tuyển dụng**: doanh nghiệp, hợp tác xã, hộ kinh doanh hoặc cá nhân kinh doanh sử dụng website tuyendung.chotca.com để đăng tin và tiếp cận Người tìm việc.",
        "**Tin tuyển dụng**: nội dung do Nhà tuyển dụng đăng tải để tìm người lao động.",
        "**Đơn ứng tuyển**: hành động Người tìm việc chủ động bày tỏ quan tâm tới một Tin tuyển dụng.",
        "**Lời mời phỏng vấn**: đề nghị của Nhà tuyển dụng gửi tới Người tìm việc kèm địa điểm và các khung giờ phỏng vấn cụ thể.",
        "**Kho ứng viên**: tập hợp hồ sơ của những Người tìm việc đã tự bật chế độ **Đang tìm việc** trong ứng dụng.",
        "**Lượt săn ứng viên**: đơn vị tính phí bị trừ khi Nhà tuyển dụng chủ động gửi lời mời phỏng vấn tới một người trong Kho ứng viên.",
        "**Tài khoản**: hồ sơ người dùng trên Nền tảng. Một địa chỉ email tương ứng với một tài khoản; một tài khoản chủ tương ứng với một doanh nghiệp.",
      ],
    },
    {
      title: "2. Bản chất dịch vụ và vị thế pháp lý của Chốt Ca",
      body: `Chốt Ca là **nền tảng công nghệ kết nối thông tin tuyển dụng**. Chúng tôi cung cấp công cụ để Nhà tuyển dụng đăng tin và để Người tìm việc tiếp cận tin, ứng tuyển, nhận lời mời phỏng vấn. Cụ thể, Chốt Ca xác định rõ:`,
      items: [
        "Chốt Ca **không** là người sử dụng lao động và **không** là một bên trong hợp đồng lao động giữa Nhà tuyển dụng và Người tìm việc.",
        "Chốt Ca **không** tổ chức phỏng vấn, không đào tạo, không phân ca, không quản lý công việc, không đánh giá kết quả lao động.",
        "Chốt Ca **không giữ lương, không thu hộ, không chi hộ, không ký quỹ** và không đứng tên trong bất kỳ giao dịch tiền lương nào giữa hai bên. Vì không cầm giữ tiền của người khác, Chốt Ca không thuộc diện phải có Giấy phép hoạt động cung ứng dịch vụ trung gian thanh toán.",
        "Chốt Ca **không thu bất kỳ khoản phí nào từ Người tìm việc**. Toàn bộ tính năng dành cho Người tìm việc là miễn phí.",
        "Doanh thu duy nhất của Chốt Ca là phí dịch vụ nền tảng thu từ Nhà tuyển dụng, chi tiết tại [Điều khoản thanh toán](/payment-terms).",
        "Mọi quan hệ lao động phát sinh giữa hai bên là quan hệ độc lập với Chốt Ca, được điều chỉnh bởi **Bộ luật Lao động 2019**, **Luật Việc làm 2025** và các văn bản pháp luật liên quan.",
      ],
    },
    {
      title: "3. Điều kiện sử dụng và độ tuổi",
      sections: [
        {
          title: "3.1. Điều kiện chung",
          items: [
            "Có năng lực hành vi dân sự phù hợp theo Bộ luật Dân sự 2015 để xác lập giao dịch trên Nền tảng.",
            "Cung cấp thông tin chính xác, trung thực, cập nhật khi đăng ký và trong suốt quá trình sử dụng.",
            "Chỉ vận hành **một (1) tài khoản** cho một (1) cá nhân. Một tài khoản chủ tương ứng với một (1) doanh nghiệp.",
          ],
        },
        {
          title: "3.2. Độ tuổi tối thiểu của Người tìm việc",
          body: "Chốt Ca áp dụng giới hạn độ tuổi theo Bộ luật Lao động 2019. Vì hồ sơ chỉ lưu **năm sinh**, tuổi được tính theo năm.",
          items: [
            "**Dưới 15 tuổi**: không được tạo hồ sơ Người tìm việc trên Chốt Ca. Căn cứ Điều 3 khoản 1 và Điều 143 Bộ luật Lao động 2019, người từ đủ 13 đến chưa đủ 15 tuổi chỉ được làm công việc nhẹ thuộc danh mục do Bộ Lao động ban hành, danh mục này không bao gồm các vị trí F&B đăng trên Nền tảng.",
            "**Từ đủ 15 đến chưa đủ 18 tuổi**: được tạo hồ sơ và ứng tuyển, nhưng chịu các giới hạn tại mục 3.3.",
            "**Từ đủ 18 tuổi**: không giới hạn.",
          ],
        },
        {
          title: "3.3. Giới hạn đối với người chưa thành niên",
          body: "Nếu bạn từ đủ 15 đến chưa đủ 18 tuổi, bạn **không được ứng tuyển** và Nhà tuyển dụng **không được tuyển bạn** vào:",
          items: [
            "Công việc sản xuất, kinh doanh cồn, rượu, bia, thuốc lá, chất tác động đến tinh thần hoặc chất gây nghiện khác — Điều 147 khoản 1 điểm b Bộ luật Lao động 2019. Trong ngành F&B, giới hạn này bao gồm các vị trí trực tiếp pha chế hoặc bán đồ uống có cồn.",
            "Nơi làm việc là quán bar, vũ trường, phòng hát karaoke, sòng bạc, khách sạn, nhà nghỉ, cơ sở tắm hơi, cơ sở xoa bóp, điểm kinh doanh xổ số, dịch vụ trò chơi điện tử — Điều 147 khoản 2 điểm d Bộ luật Lao động 2019.",
            "Ca làm việc ban đêm trong khung **22:00 đến 06:00** — Điều 146 khoản 1 và Điều 105 Bộ luật Lao động 2019.",
            "Công việc vượt quá **08 giờ mỗi ngày** hoặc **40 giờ mỗi tuần** — Điều 146 khoản 2 Bộ luật Lao động 2019.",
          ],
        },
        {
          title: "3.4. Độ tuổi của Nhà tuyển dụng",
          body: "Người đăng ký và vận hành tài khoản Nhà tuyển dụng phải **từ đủ 18 tuổi**, có đầy đủ năng lực hành vi dân sự và có thẩm quyền đại diện cho doanh nghiệp hoặc cơ sở kinh doanh mà mình đăng ký.",
        },
        {
          title: "3.5. Khi phát hiện vi phạm độ tuổi",
          body: `Chốt Ca có quyền yêu cầu bổ sung thông tin, gỡ tin tuyển dụng, hạn chế tính năng hoặc khoá tài khoản khi có căn cứ cho rằng quy định về độ tuổi bị vi phạm. Nếu bạn là cha, mẹ hoặc người giám hộ và phát hiện người chưa đủ tuổi đã tạo tài khoản, vui lòng gửi yêu cầu tới ${C.dpoEmail} để chúng tôi xử lý.`,
        },
      ],
    },
    {
      title: "4. Tài khoản, đăng nhập và bảo mật",
      body: `Chốt Ca sử dụng **email** làm định danh tài khoản. Bạn đăng nhập bằng email kèm mật khẩu, bằng mã xác thực một lần (OTP) gửi qua email, hoặc bằng tài khoản Google hoặc Apple. Chốt Ca **không** gửi OTP qua tin nhắn SMS hay Zalo, nên mọi tin nhắn SMS tự xưng là mã OTP của Chốt Ca đều là giả mạo.\n\nBạn chịu trách nhiệm bảo mật mật khẩu, mã OTP và mọi hoạt động dưới tài khoản của mình. Vui lòng thông báo ngay cho chúng tôi qua **${C.email}** nếu phát hiện tài khoản bị truy cập trái phép. Chốt Ca không chịu trách nhiệm về thiệt hại phát sinh do bạn không bảo mật thông tin đăng nhập của mình.`,
    },
    {
      title:
        "5. Cách Nền tảng vận hành và thông tin của bạn được chia sẻ khi nào",
      body: "Đây là phần quan trọng nhất để bạn hiểu điều gì xảy ra với thông tin liên hệ của mình. Chi tiết đầy đủ tại [Chính sách bảo vệ dữ liệu cá nhân](/privacy).",
      sections: [
        {
          title: "5.1. Khi bạn chủ động ứng tuyển",
          items: [
            "Ngay khi bạn bấm ứng tuyển một tin, Nhà tuyển dụng đăng tin đó nhận được **toàn bộ hồ sơ** của bạn, bao gồm **họ tên đầy đủ, số điện thoại và email**, ngay lập tức và không cần thêm bước xác nhận nào.",
            "Việc bạn bấm ứng tuyển chính là sự đồng ý của bạn cho việc chia sẻ này, đồng thời là bước cần thiết để thực hiện dịch vụ kết nối mà bạn yêu cầu.",
            "Nhà tuyển dụng mời bạn phỏng vấn sau khi bạn ứng tuyển thì **không mất phí** cho Chốt Ca.",
            "Bạn **rút đơn** thì được ứng tuyển lại tin đó khi tin còn mở. Đơn đã bị Nhà tuyển dụng **từ chối** là điểm dừng, bạn không ứng tuyển lại tin đó được.",
          ],
        },
        {
          title: "5.2. Khi bạn bật chế độ Đang tìm việc",
          items: [
            "Hồ sơ của bạn xuất hiện trong Kho ứng viên ở **dạng che**: tên viết tắt, ảnh đại diện, năm sinh, quận đang ở, vị trí mong muốn, kỹ năng và kinh nghiệm. Nhà tuyển dụng **không thấy** họ tên đầy đủ, số điện thoại, email, Zalo, Facebook hay Instagram của bạn.",
            "Nhà tuyển dụng gửi lời mời phỏng vấn tới bạn thì thông tin của bạn **vẫn ở dạng che**. Họ chưa thấy số điện thoại của bạn ở bước này.",
            "Thông tin liên hệ của bạn **chỉ được mở khi bạn bấm nhận lời mời và chọn một khung giờ phỏng vấn**. Bạn từ chối hoặc để lời mời hết hạn thì Nhà tuyển dụng không bao giờ thấy thông tin liên hệ của bạn.",
            "Bạn **tắt chế độ Đang tìm việc** bất cứ lúc nào để rời khỏi Kho ứng viên, và **ẩn một Nhà tuyển dụng cụ thể** để họ không thấy và không mời được bạn.",
          ],
        },
        {
          title: "5.3. Vòng đời lời mời phỏng vấn",
          items: [
            "Lời mời có hiệu lực **24 giờ**. Quá hạn không trả lời thì lời mời tự hết hiệu lực.",
            "Sau khi bạn nhận lời mời, bạn có **30 phút** để đổi ý mà không bị tính là thất hẹn.",
            "Hết 30 phút, lịch phỏng vấn được chốt và hệ thống lưu một bản sao thông tin liên hệ của cả hai bên vào lời mời, để nếu sau này một bên sửa hồ sơ thì bên kia vẫn giữ được thông tin đã trao đổi.",
            "Sau khi chốt lịch, hai bên liên hệ trực tiếp với nhau qua điện thoại hoặc Zalo. Chốt Ca **không có tính năng nhắn tin trong ứng dụng**.",
            "Một Người tìm việc chỉ giữ tối đa **5 lời mời đang chờ trả lời** cùng lúc trên toàn Nền tảng.",
          ],
        },
      ],
    },
    {
      title: "6. Quy tắc đăng tin và ứng tuyển",
      sections: [
        {
          title: "6.1. Đối với Nhà tuyển dụng",
          items: [
            "Tin đăng phải mô tả đúng vị trí công việc thực tế tại cơ sở kinh doanh hợp pháp của bạn.",
            "Mức lương, loại hình công việc, ca làm, địa điểm, yêu cầu công việc phải minh bạch và đúng sự thật.",
            "Không đăng tuyển công việc trái pháp luật, đa cấp, lừa đảo, hoặc dưới hình thức môi giới lao động bất hợp pháp.",
            "**Không thu bất kỳ khoản tiền nào từ Người tìm việc** dưới mọi hình thức, gồm tiền đặt cọc, phí hồ sơ, phí đồng phục, phí đào tạo, trước khi có hợp đồng lao động hợp pháp.",
            "**Không phân biệt đối xử trong lao động** — Điều 8 khoản 1 Bộ luật Lao động 2019 và Điều 5 khoản 1 Luật Việc làm 2025. Chi tiết tại [Thoả thuận dành cho Nhà tuyển dụng](/employer-agreement).",
            "Không chèn số điện thoại, Zalo hoặc đường dẫn ngoài vào tiêu đề và mô tả tin nhằm kéo người dùng ra khỏi Nền tảng.",
          ],
        },
        {
          title: "6.2. Đối với Người tìm việc",
          items: [
            "Hồ sơ phải phản ánh đúng kinh nghiệm, kỹ năng và tình trạng thực tế của bạn.",
            "Không sử dụng giấy tờ tuỳ thân, ảnh hoặc danh tính của người khác. Không tạo hồ sơ giả mạo.",
            "Tôn trọng Nhà tuyển dụng. Nhận lời mời phỏng vấn thì có mặt đúng giờ, hoặc báo huỷ sớm nhất có thể.",
            "Không đòi hỏi tiền hoặc lợi ích cá nhân từ Nhà tuyển dụng ngoài các thoả thuận lao động hợp pháp.",
            "Không ứng tuyển hàng loạt để chiếm chỗ. Chốt Ca giới hạn số đơn ứng tuyển mỗi ngày và có thể siết giới hạn khi phát hiện hành vi bất thường.",
          ],
        },
      ],
    },
    {
      title: "7. Phí dịch vụ",
      body: "Người tìm việc sử dụng Nền tảng **miễn phí hoàn toàn**, không có gói trả phí, không có tính năng khoá sau tường phí. Nhà tuyển dụng đăng tin miễn phí và trả phí theo **gói thuê bao 30 ngày** để mở rộng hạn mức tin đăng, lượt săn ứng viên và lượt đẩy tin. Gói **không tự động gia hạn** và Chốt Ca **không hoàn tiền**. Toàn bộ quy định về giá, thanh toán, hoá đơn và khiếu nại giao dịch nằm tại [Điều khoản thanh toán](/payment-terms).",
    },
    {
      title: "8. Hành vi bị cấm",
      body: "Khi sử dụng Nền tảng, bạn cam kết KHÔNG:",
      items: [
        "Đăng tải nội dung sai sự thật, lừa đảo, quảng cáo trá hình, vi phạm pháp luật Việt Nam.",
        "Quấy rối, đe doạ, xúc phạm, kỳ thị, phát ngôn thù hằn đối với người khác.",
        "**Mua, bán, trao đổi dữ liệu cá nhân** của Người dùng khác. Đây là hành vi bị nghiêm cấm theo Điều 7 khoản 6 Luật Bảo vệ dữ liệu cá nhân 2025.",
        "Thu thập, sao chép, khai thác hoặc phát tán dữ liệu người dùng và thông tin thị trường lao động trên Nền tảng ngoài mục đích tuyển dụng hợp pháp — Điều 5 khoản 4 Luật Việc làm 2025.",
        "Sử dụng bot, script, trình thu thập dữ liệu tự động để truy cập, sao chép hoặc can thiệp Nền tảng.",
        "Đăng nội dung vi phạm quyền tác giả, nhãn hiệu, quyền sở hữu công nghiệp của bên thứ ba.",
        "Tuyển dụng cho công việc thuộc danh mục pháp luật cấm hoặc trái thuần phong mỹ tục.",
        "Sử dụng Nền tảng để tổ chức, môi giới đưa người lao động đi làm việc ở nước ngoài trái phép.",
        "Mạo danh nhân viên, đại diện hoặc thương hiệu của Chốt Ca.",
      ],
    },
    {
      title: "9. Xác minh danh tính",
      sections: [
        {
          title: "9.1. Người tìm việc",
          body: "Tính năng xác minh danh tính bằng giấy tờ tuỳ thân đối với Người tìm việc **hiện đang tắt**. Chốt Ca **không thu thập ảnh Căn cước công dân và ảnh chân dung xác thực** của Người tìm việc, và bạn không cần xác minh danh tính để tạo hồ sơ hay ứng tuyển. Nếu tính năng này được bật lại trong tương lai, chúng tôi sẽ xin sự đồng ý riêng của bạn trước khi thu thập và cập nhật [Chính sách bảo vệ dữ liệu cá nhân](/privacy) tương ứng.",
        },
        {
          title: "9.2. Nhà tuyển dụng",
          body: "Xác minh doanh nghiệp là **tuỳ chọn** và **không bắt buộc để đăng tin**. Nhà tuyển dụng nộp mã số thuế và giấy chứng nhận đăng ký kinh doanh, quản trị viên Chốt Ca duyệt thủ công. Chốt Ca không sử dụng dịch vụ xác minh giấy tờ tự động của bên thứ ba. Ngoài ra, quản trị viên có thể gắn huy hiệu tín nhiệm cho một số Nhà tuyển dụng. Huy hiệu này mang tính biên tập, **không phải** kết quả xác minh pháp lý và không phải cam kết bảo đảm của Chốt Ca.",
        },
      ],
    },
    {
      title: "10. Nội dung của bạn và quyền sở hữu trí tuệ",
      items: [
        `Toàn bộ thương hiệu "${C.brandName}", logo, giao diện, mã nguồn và nội dung do chúng tôi sản xuất là tài sản của ${C.legalName}, được bảo hộ theo Luật Sở hữu trí tuệ.`,
        "Bạn giữ quyền đối với nội dung mình đăng tải, gồm tin tuyển dụng, ảnh cơ sở kinh doanh và hồ sơ cá nhân.",
        "Khi đăng tải nội dung, bạn cấp cho Chốt Ca giấy phép không độc quyền, miễn phí bản quyền, có hiệu lực trong phạm vi lãnh thổ Việt Nam, để lưu trữ, hiển thị và sao chép kỹ thuật nội dung đó nhằm mục đích vận hành Nền tảng. Giấy phép này chấm dứt khi bạn gỡ nội dung hoặc xoá tài khoản, trừ các bản sao lưu kỹ thuật còn tồn tại trong chu kỳ xoá đã công bố.",
        "Bạn cam kết nội dung mình đăng không xâm phạm quyền của bên thứ ba.",
      ],
    },
    {
      title: "11. Tạm khoá và chấm dứt tài khoản",
      body: "Chúng tôi có quyền cảnh cáo, hạn chế tính năng, gỡ nội dung, tạm khoá hoặc chấm dứt tài khoản trong các trường hợp:",
      items: [
        "Vi phạm Điều khoản này hoặc [Quy tắc cộng đồng](/community-guidelines).",
        "Có dấu hiệu lừa đảo, gian lận, sử dụng thông tin giả mạo.",
        "Bị Người dùng khác báo cáo nhiều lần với bằng chứng xác thực.",
        "Theo yêu cầu hợp pháp bằng văn bản của cơ quan nhà nước có thẩm quyền.",
        "Bạn tự yêu cầu xoá tài khoản theo [Hướng dẫn xoá dữ liệu](/data-deletion).",
      ],
    },
    {
      title: "12. Giới hạn trách nhiệm",
      body: "Chốt Ca cam kết vận hành Nền tảng một cách hợp lý và xử lý báo cáo vi phạm. Trong phạm vi pháp luật cho phép, Chốt Ca KHÔNG chịu trách nhiệm về:",
      items: [
        "Kết quả tuyển dụng, nội dung hợp đồng lao động, lương, thưởng, bảo hiểm xã hội, kỷ luật lao động hay tai nạn lao động giữa Nhà tuyển dụng và Người tìm việc.",
        "Tính chính xác của thông tin do Người dùng tự khai, dù chúng tôi có cơ chế kiểm duyệt và xử lý báo cáo.",
        "Hành vi của Người dùng khác trên hoặc ngoài Nền tảng.",
        "Gián đoạn dịch vụ hoặc mất mát dữ liệu do sự kiện bất khả kháng, gồm thiên tai, mất điện, sự cố nhà cung cấp hạ tầng, tấn công mạng vượt quá khả năng phòng vệ hợp lý.",
        "Thiệt hại gián tiếp, lợi nhuận bị mất, cơ hội kinh doanh bị mất.",
      ],
    },
    {
      title: "13. Bồi thường",
      body: "Bạn đồng ý bồi thường cho Chốt Ca đối với các khiếu nại, tổn thất và chi phí hợp lý phát sinh trực tiếp từ việc bạn vi phạm Điều khoản này hoặc xâm phạm quyền của bên thứ ba. Nghĩa vụ này không áp dụng cho phần thiệt hại do lỗi của chính Chốt Ca.",
    },
    {
      title: "14. Sửa đổi Điều khoản",
      body: `Chúng tôi có thể cập nhật Điều khoản theo thời gian. Bản mới có hiệu lực kể từ ngày ghi trên đầu trang tại ${C.websiteUrl}/terms. Với thay đổi quan trọng, gồm mở rộng phạm vi thu thập dữ liệu hoặc thay đổi cấu trúc phí, chúng tôi thông báo qua ứng dụng hoặc email tối thiểu **15 ngày** trước ngày hiệu lực. Việc bạn tiếp tục sử dụng Nền tảng sau ngày hiệu lực được xem là chấp nhận phiên bản mới. Nếu không đồng ý, bạn có quyền ngừng sử dụng và xoá tài khoản.`,
    },
    {
      title: "15. Luật áp dụng và giải quyết tranh chấp",
      body: `Điều khoản này được điều chỉnh bởi pháp luật Việt Nam. Mọi tranh chấp phát sinh được ưu tiên giải quyết bằng thương lượng và hoà giải; bạn gửi khiếu nại trước tới ${C.legalEmail} và chúng tôi phản hồi trong **15 ngày làm việc**. Nếu không đạt được thoả thuận, tranh chấp được đưa ra **Toà án nhân dân có thẩm quyền tại Thành phố Hồ Chí Minh**. Điều khoản này không hạn chế quyền khiếu nại, tố cáo hoặc khởi kiện của bạn theo Luật Bảo vệ quyền lợi người tiêu dùng 2023 và pháp luật liên quan.`,
    },
  ],
  contact: {
    title: "16. Liên hệ",
    body: [
      C.legalName,
      C.taxId ? `Mã số doanh nghiệp: ${C.taxId}` : null,
      `Địa chỉ trụ sở: ${C.address}`,
      `Người đại diện theo pháp luật: ${C.representative}`,
    ]
      .filter(Boolean)
      .join("\n"),
    prefix: "Mọi thắc mắc về Điều khoản xin gửi về ",
    email: C.legalEmail,
  },
};
